import {
  getAiChatMessageItem,
  getAiChatSuggestionItem,
  getDocumentPlaybookRoute,
  link,
} from '@aline/routes';
import type { Code, Link } from 'mdast';
import { z } from 'zod';

import { loadRemarkProcessor } from './unified-loader';
import type { ParsedSuggestionItem } from '../../agent-harness/suggestion-code-parser';
import { parseJsonlToSuggestionList } from '../../agent-harness/suggestion-code-parser';
import { parseHtmlDraftArtifacts } from '../../agent-harness/writer-artifacts';
import { AgentSkillAiResultSchema } from '../../agent-skills/skill-ai';
import {
  getSkillAuthoringPresentation,
  getSkillAuthoringToolOutput,
} from '../../agent-skills/skill-authoring-presentation';
import { aiAlertSchema } from '../../chat';
import { getToolApprovalForToolCall } from '../../chat/ai-sdk-ui-messages';
import {
  getExternalConnectDescription,
  getExternalConnectTitle,
  getExternalToolCallTitle,
  getExternalToolkitLabel,
  getExternalToolResolutionLabel,
} from '../../chat/external-tool-labels';
import type { ToolCall } from '../../chat/tool-calls';
import { toolCallSchema } from '../../chat/tool-calls';
import { aiErrorSchema, aiMimeTypes } from '../../constants';
import { parseMarkdownLocationLink } from '../../markdown-location-link';
import { PlaybookAiRunResultSchema } from '../../playbook-ai';
import type { CodeBlockRedlineItem } from '../../redlines/redline-shapes';
import { CodeBlockRedlineItemSchema } from '../../redlines/redline-shapes';
import { ReportAiGenerationResultSchema } from '../../report-ai';
import { getReportCreationPresentation } from '../../report-creation-presentation';
import { createDocumentRoute, getDocumentRoute, getWorkflowRoute } from '../../routes';
import { groupByAsMap } from '../../util';
import type {
  WorkflowAiCandidate,
  WorkflowAiResult,
  WorkflowAiToolCallResult,
} from '../../workflow-ai';
import { WorkflowAiResultSchema } from '../../workflow-ai';

type AiMimeType = (typeof aiMimeTypes)[keyof typeof aiMimeTypes];

const INTERNAL_AI_MIME_TYPES = new Set<string>(Object.values(aiMimeTypes));

function getIsInternalAiMimeType(mimeType: string): mimeType is AiMimeType {
  return INTERNAL_AI_MIME_TYPES.has(mimeType);
}

export interface SerializeAiChatMarkdownOptions {
  baseAppUrl: string;
  playbookRunArtifacts: PlaybookRunArtifactMetadata[];
  taskArtifacts: TaskArtifactMetadata[];
  workflowArtifacts: WorkflowArtifactMetadata[];
  workflowTemplateArtifacts: WorkflowTemplateArtifactMetadata[];
  aiChatPath?: string;
  assistantMessageRunning?: boolean;
  messageId?: string;
  omitToolCalls?: boolean;
  processingToolCallIds?: readonly string[];
  threadId?: string;
  uiParts?: unknown[] | null;
}

export interface PlaybookRunArtifactMetadata {
  documentId: string;
  documentTitle: string;
  playbookId: string;
  playbookTitle: string;
}

export interface TaskArtifactMetadata {
  taskId: string;
  taskTitle: string;
}

export interface WorkflowTemplateArtifactMetadata {
  templateTitle: string;
  templateWorkflowId: string;
}

export interface WorkflowArtifactMetadata {
  workflowId: string;
  workflowTitle: string;
}

interface SerializationContext extends SerializeAiChatMarkdownOptions {
  playbookRunArtifactsByKey: Map<string, PlaybookRunArtifactMetadata>;
  renderedSkillLinks: Set<string>;
  taskArtifactsById: Map<string, TaskArtifactMetadata>;
  workflowArtifactsById: Map<string, WorkflowArtifactMetadata>;
  workflowTemplateArtifactsById: Map<string, WorkflowTemplateArtifactMetadata>;
}

type SerializationMode = 'channel-plan' | 'channel-reply' | 'copy' | 'plain-text';

interface MarkdownReplacement {
  end: number;
  start: number;
  value: string;
}

interface PositionedNode {
  position?: {
    end: { offset?: number };
    start: { offset?: number };
  };
}

export async function serializeAiChatMarkdownForChannelPlan(
  rawText: string,
  options: SerializeAiChatMarkdownOptions
) {
  return serializeAiChatMarkdown(rawText, options, 'channel-plan');
}

export async function serializeAiChatMarkdownForChannelReply(
  rawText: string,
  options: SerializeAiChatMarkdownOptions
) {
  return serializeAiChatMarkdown(rawText, options, 'channel-reply');
}

export async function serializeAiChatMarkdownForCopy(
  rawText: string,
  options: SerializeAiChatMarkdownOptions
) {
  return serializeAiChatMarkdown(rawText, options, 'copy');
}

export async function serializeAiChatMarkdownPlainText(
  rawText: string,
  options: SerializeAiChatMarkdownOptions
) {
  return serializeAiChatMarkdown(rawText, options, 'plain-text');
}

async function serializeAiChatMarkdown(
  rawText: string,
  options: SerializeAiChatMarkdownOptions,
  mode: SerializationMode
) {
  const context = createSerializationContext(options);
  const { baseProcessor } = await loadRemarkProcessor();
  const { visit } = await import('unist-util-visit');
  const tree = baseProcessor.parse(rawText);
  const codeReplacements: MarkdownReplacement[] = [];

  // A legacy result card may precede the tool block that now carries the saved result.
  if (!context.omitToolCalls)
    visit(tree, 'code', (node: Code) => {
      if (node.lang !== aiMimeTypes.jsonToolCall) return;
      const parsed = toolCallSchema.safeParse(parseJson(node.value));
      if (!parsed.success) return;
      const toolCall = parsed.data;
      if (toolCall.type !== 'createSkill' && toolCall.type !== 'updateSkill') return;
      const presentation = getSkillToolPresentation(toolCall, context);
      if (presentation.skill) context.renderedSkillLinks.add(presentation.skill.id);
    });

  visit(tree, 'code', (node: Code) => {
    const range = getNodeRange(node);
    if (!range) return;

    const replacement = formatStructuredCodeBlock(node, context, mode);
    if (replacement === null) return;
    codeReplacements.push({ ...range, value: replacement });
  });

  const withoutInternalBlocks = applyMarkdownReplacements(rawText, codeReplacements);
  const linkTree = baseProcessor.parse(withoutInternalBlocks);
  const linkReplacements: MarkdownReplacement[] = [];

  visit(linkTree, 'link', (node: Link) => {
    const range = getNodeRange(node);
    if (!range) return;

    const label = getNodeText(node);
    const value = formatLink(label, node.url, context, mode);
    if (value === null) return;
    linkReplacements.push({ ...range, value });
  });

  return applyMarkdownReplacements(withoutInternalBlocks, linkReplacements)
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function createSerializationContext(options: SerializeAiChatMarkdownOptions): SerializationContext {
  return {
    ...options,
    playbookRunArtifactsByKey: new Map(
      options.playbookRunArtifacts.map(artifact => [
        `${artifact.documentId}:${artifact.playbookId}`,
        artifact,
      ])
    ),
    renderedSkillLinks: new Set(),
    taskArtifactsById: new Map(options.taskArtifacts.map(artifact => [artifact.taskId, artifact])),
    workflowArtifactsById: new Map(
      options.workflowArtifacts.map(artifact => [artifact.workflowId, artifact])
    ),
    workflowTemplateArtifactsById: new Map(
      options.workflowTemplateArtifacts.map(artifact => [artifact.templateWorkflowId, artifact])
    ),
  };
}

function formatStructuredCodeBlock(
  node: Code,
  options: SerializationContext,
  mode: SerializationMode
) {
  const mimeType = node.lang;
  if (!mimeType || !getIsInternalAiMimeType(mimeType)) return null;

  switch (mimeType) {
    case aiMimeTypes.jsonAgentSkill:
      return formatAgentSkillBlock(node.value, options);
    case aiMimeTypes.htmlDraftDocument:
      return formatDraftDocumentBlock(node.value, options);
    case aiMimeTypes.csvSuggestionList:
      return formatAlineAiChatArtifact(
        'Aline generated suggested edits',
        options,
        'Review the edits in Aline AI Chat.'
      );
    case aiMimeTypes.jsonAlert:
      return formatAlertBlock(node.value, options);
    case aiMimeTypes.jsonError:
      return formatErrorBlock(node.value, options);
    case aiMimeTypes.jsonPlaybookGeneration:
      return formatPlaybookGenerationBlock(options);
    case aiMimeTypes.jsonPlaybookRun:
      return formatPlaybookRunBlock(node.value, options);
    case aiMimeTypes.jsonReportGeneration:
      return formatReportGenerationBlock(node.value, options);
    case aiMimeTypes.jsonToolCall:
      return options.omitToolCalls ? '' : formatToolCallBlock(node.value, options);
    case aiMimeTypes.jsonWorkflowResult:
      if (mode === 'channel-reply') return '';
      return formatWorkflowResultBlock(node.value, options);
    case aiMimeTypes.jsonlRedlineEdit:
      return formatRedlineEditBlock(node.value, options);
    case aiMimeTypes.jsonlSuggestionList:
      return formatSuggestionListBlock(node.value, options);
    case aiMimeTypes.markdownDraftDocument:
      return formatMarkdownDraftBlock(node.value, options);
    case aiMimeTypes.plainText:
      return node.value;
    case aiMimeTypes.textThinking:
      return '';
  }
}

function formatDraftDocumentBlock(rawBody: string, options: SerializeAiChatMarkdownOptions) {
  const drafts = parseHtmlDraftArtifacts(rawBody);
  if (!drafts.length) {
    return formatAlineAiChatArtifact(
      'Aline drafted a document',
      options,
      'Review the draft in Aline AI Chat.'
    );
  }

  return drafts
    .map(draft => {
      const url = getAbsoluteAppUrl(createDocumentRoute(draft.documentId), options.baseAppUrl);
      return formatArtifact(
        `A document titled [${escapeMarkdownLinkLabel(draft.title)}](${url}) was drafted.`
      );
    })
    .join('\n');
}

function formatAgentSkillBlock(rawJson: string, options: SerializationContext) {
  const result = AgentSkillAiResultSchema.safeParse(parseJson(rawJson));
  if (!result.success)
    return formatAlineAiChatArtifact('Aline created a skill', options, 'Open it in Aline.');

  const { action, name, skillId } = result.data;
  if (options.renderedSkillLinks.has(skillId)) return '';
  options.renderedSkillLinks.add(skillId);
  const skillLink = formatSkillLink({ id: skillId, name: name || 'Untitled skill' }, options);
  return formatArtifact(`The skill ${skillLink} was ${action}.`);
}

function formatAlertBlock(rawJson: string, options: SerializeAiChatMarkdownOptions) {
  const result = aiAlertSchema.safeParse(parseJson(rawJson));
  if (!result.success) {
    return formatAlineAiChatSummary('Aline posted an alert', options, 'Open Aline for details.');
  }
  return `*${result.data.title}*\n${result.data.message}`;
}

function formatErrorBlock(rawJson: string, options: SerializeAiChatMarkdownOptions) {
  const result = aiErrorSchema.safeParse(parseJson(rawJson));
  if (!result.success) {
    return formatAlineAiChatSummary(
      'Aline encountered an error',
      options,
      'Open Aline for details.'
    );
  }
  return `*${result.data.title}*\n${result.data.message}`;
}

/**
 * Unlike a run, a generation has nothing to link to. The fence carries the
 * generation job's id, and the playbook the job writes does not exist until it
 * finishes — normally long after a reply has been serialized and sent. So the
 * block says what started and points back at the thread, where the card fills
 * itself in.
 */
function formatPlaybookGenerationBlock(options: SerializeAiChatMarkdownOptions) {
  return formatAlineAiChatArtifact(
    'Aline started generating a playbook',
    options,
    'Track its progress in Aline AI Chat.'
  );
}

function formatPlaybookRunBlock(rawJson: string, options: SerializationContext) {
  const result = PlaybookAiRunResultSchema.safeParse(parseJson(rawJson));
  if (!result.success) {
    return formatAlineAiChatArtifact(
      'Aline started a playbook run',
      options,
      'Track its progress in Aline AI Chat.'
    );
  }

  const playbookRunPath = getDocumentPlaybookRoute({
    documentId: result.data.documentId,
    playbookId: result.data.playbookId,
  });
  const playbookRunUrl = getAbsoluteAppUrl(playbookRunPath, options.baseAppUrl);
  const metadata = options.playbookRunArtifactsByKey.get(
    `${result.data.documentId}:${result.data.playbookId}`
  );
  if (metadata) {
    const label = `"${metadata.playbookTitle}" playbook for "${metadata.documentTitle}"`;
    return formatArtifact(`The [${escapeMarkdownLinkLabel(label)}](${playbookRunUrl}) was run.`);
  }
  return formatArtifact(`[Open the playbook run](${playbookRunUrl}).`);
}

function formatReportGenerationBlock(rawJson: string, options: SerializeAiChatMarkdownOptions) {
  const result = ReportAiGenerationResultSchema.safeParse(parseJson(rawJson));
  if (!result.success) {
    return formatAlineAiChatArtifact(
      'Aline created a report',
      options,
      'Open it from Aline AI Chat.'
    );
  }

  const { columnCount, name } = result.data;
  return formatAlineAiChatArtifact(
    `Aline created report "${name}"`,
    options,
    `${columnCount} ${pluralize('column', columnCount)}. Open it from Aline AI Chat.`
  );
}

function formatRedlineEditBlock(rawJsonl: string, options: SerializeAiChatMarkdownOptions) {
  const items = parseRedlineItems(rawJsonl);
  if (!items.length) {
    return formatAlineAiChatArtifact(
      'Aline generated redlines',
      options,
      'Review the redlines in Aline AI Chat.'
    );
  }

  return [...groupByAsMap(item => item.documentId, items)]
    .map(([documentId, documentItems]) =>
      formatRedlineDocumentSummary(documentId, documentItems, options)
    )
    .join('\n\n');
}

function parseRedlineItems(rawJsonl: string) {
  return rawJsonl.split('\n').flatMap(line => {
    if (!line.trim()) return [];
    const result = CodeBlockRedlineItemSchema.safeParse(parseJson(line));
    return result.success ? [result.data] : [];
  });
}

function formatSuggestionListBlock(rawJsonl: string, options: SerializeAiChatMarkdownOptions) {
  const items = parseJsonlToSuggestionList(rawJsonl);
  if (!items.length) {
    return formatAlineAiChatArtifact(
      'Aline generated suggested edits',
      options,
      'Review the edits in Aline AI Chat.'
    );
  }

  return [...groupByAsMap(item => item.documentId, items)]
    .map(([documentId, documentItems]) =>
      formatSuggestionDocumentSummary(documentId, documentItems, options)
    )
    .join('\n\n');
}

function formatRedlineDocumentSummary(
  documentId: string,
  items: CodeBlockRedlineItem[],
  options: SerializeAiChatMarkdownOptions
) {
  const editCount = items.reduce(
    (count, item) => count + (item.type === 'redline-edit' ? item.actions.length : 0),
    0
  );
  const firstGroupId = items.find(item => item.type === 'redline-edit')?.actions.at(0)?.groupId;
  const documentUrl = getSuggestedEditDocumentUrl(documentId, {
    ...options,
    groupId: firstGroupId,
  });
  return formatArtifact(
    `Aline generated ${editCount} ${pluralize('redline', editCount)} for [this document](${documentUrl}).`
  );
}

function formatSuggestionDocumentSummary(
  documentId: string,
  items: ParsedSuggestionItem[],
  options: SerializeAiChatMarkdownOptions
) {
  const editCount = items.filter(item => item.type === 'edit').length;
  const title = items[0]?.title || 'Untitled Document';
  const firstGroupId = items.find(item => item.type === 'edit')?.groupId;
  const documentUrl = getSuggestedEditDocumentUrl(documentId, {
    ...options,
    groupId: firstGroupId,
  });
  return formatArtifact(
    `Aline generated ${editCount} suggested ${pluralize('edit', editCount)} for [${escapeMarkdownLinkLabel(title)}](${documentUrl}).`
  );
}

function getSuggestedEditDocumentUrl(
  documentId: string,
  options: SerializeAiChatMarkdownOptions & { groupId?: string }
) {
  return getAbsoluteAppUrl(
    createDocumentRoute(documentId, {
      aiChatItem: getSuggestionAiChatItem(options),
    }),
    options.baseAppUrl
  );
}

function formatMarkdownDraftBlock(rawBody: string, options: SerializeAiChatMarkdownOptions) {
  const title = rawBody.match(/^---\r?\n[\s\S]*?^title:\s*(.+?)\r?$\n[\s\S]*?^---$/m)?.[1]?.trim();
  return formatAlineAiChatArtifact(
    title ? `Aline drafted "${title}"` : 'Aline drafted a document',
    options,
    'Review the draft in Aline AI Chat.'
  );
}

function formatAlineAiChatArtifact(
  title: string,
  options: SerializeAiChatMarkdownOptions,
  description: string
) {
  const aiChatUrl = getAiChatArtifactUrl(options);
  return formatArtifact(`${title}\n${description}\n[Open Aline AI Chat](${aiChatUrl})`);
}

function formatAlineAiChatSummary(
  title: string,
  options: SerializeAiChatMarkdownOptions,
  description: string
) {
  const aiChatUrl = getAiChatArtifactUrl(options);
  return `*${title}*\n${description}\n[Open Aline AI Chat](${aiChatUrl})`;
}

function formatArtifact(content: string) {
  return `***Artifact:*** ${content}`;
}

function getAiChatArtifactUrl(options: SerializeAiChatMarkdownOptions) {
  const url = new URL(options.aiChatPath ?? '/chat', options.baseAppUrl);
  const aiChatItem = getMessageAiChatItem(options);
  if (aiChatItem) url.searchParams.set('aiChatItem', aiChatItem);
  return url.toString();
}

function getMessageAiChatItem({ messageId, threadId }: SerializeAiChatMarkdownOptions) {
  if (!messageId || !threadId) return undefined;
  return getAiChatMessageItem({ messageId, threadId });
}

function getSuggestionAiChatItem({
  groupId,
  messageId,
  threadId,
}: SerializeAiChatMarkdownOptions & { groupId?: string }) {
  if (!groupId || !messageId || !threadId) return undefined;
  return getAiChatSuggestionItem({ groupId, messageId, threadId });
}

function pluralize(noun: string, count: number) {
  return count === 1 ? noun : `${noun}s`;
}

function formatToolCallBlock(rawJson: string, options: SerializationContext) {
  const parsedToolCall = toolCallSchema.safeParse(parseJson(rawJson));
  if (!parsedToolCall.success) return '***Tool call:*** Unavailable.';

  const toolCall = parsedToolCall.data;
  const { description, title } = getToolCallSummary(toolCall, options);
  const result = formatInlineWorkflowSearchResult(toolCall, options);
  return [`***Tool call:*** ${title}`, description, result].filter(Boolean).join('\n');
}

function getSkillToolPresentation(
  toolCall: Extract<ToolCall, { type: 'createSkill' | 'updateSkill' }>,
  options: SerializationContext
) {
  const approval = getToolApprovalForToolCall(options.uiParts, toolCall.id);
  return getSkillAuthoringPresentation({
    approvalReason: approval?.reason,
    approved: approval?.approved,
    loading: options.processingToolCallIds?.includes(toolCall.id),
    output: getSkillAuthoringToolOutput(options.uiParts, toolCall.id),
    toolCall,
  });
}

function formatSkillLink(skill: { id: string; name: string }, options: SerializationContext) {
  const url = getAbsoluteAppUrl(
    link.AdminAgentSkillDetail({ skillId: skill.id }),
    options.baseAppUrl
  );
  return `[${escapeMarkdownLinkLabel(skill.name)}](${url})`;
}

function getToolCallSummary(
  toolCall: ToolCall,
  options: SerializationContext
): { description: string; title: string } {
  switch (toolCall.type) {
    case 'askUser':
      if (toolCall.preparing)
        return {
          description: 'Working out what information is needed.',
          title: 'Preparing questions',
        };
      return { description: 'Requested information from the user.', title: 'Asked user' };
    case 'calculator':
      return { description: `Expression: ${toolCall.expression}.`, title: 'Calculated' };
    case 'commentOnTask':
      return { description: 'Added a comment to the workflow task.', title: 'Commented on task' };
    case 'completeTask':
      return { description: 'Completed the workflow task.', title: 'Completed task' };
    case 'compactPriorOutput':
      return {
        description: `Compacted prior output from ${toolCall.sourceAgent} from ${toolCall.originalTokens} tokens toward ${toolCall.targetTokens} tokens.`,
        title: 'Compacted prior output',
      };
    case 'getWebPageContents':
      return {
        description: `URLs: ${toolCall.urls.join(', ')}.`,
        title: `Read web page contents for ${toolCall.urls.length} URL${toolCall.urls.length === 1 ? '' : 's'}`,
      };
    case 'getTask':
      return { description: 'Read the current workflow task.', title: 'Read task' };
    case 'getTaskAgentThread':
      return {
        description: 'Read the task’s AI conversation.',
        title: 'Read task conversation',
      };
    case 'modelFallback':
      return {
        description: `Retried with ${toolCall.toModel} after ${toolCall.fromModel} failed.`,
        title: 'Retried request',
      };
    case 'queryDocument':
      return {
        description: `Document: ${toolCall.documentName}\nQuery: ${getQueryDocumentDisplayQueries(toolCall).join('; ')}.`,
        title: 'Searched document',
      };
    case 'readDocument':
      return {
        description: `Document: ${toolCall.documentName || 'Untitled'}.`,
        title: 'Read document',
      };
    case 'readIntakeForm':
      return { description: 'Read the workflow intake form.', title: 'Read intake form' };
    case 'loadSkill':
      if (toolCall.failed)
        return { description: 'The skill could not be loaded.', title: 'Could not load skill' };
      return {
        description: toolCall.name ? `Skill: ${toolCall.name}.` : 'Loaded a skill for this task.',
        title: 'Loaded skill',
      };
    case 'createReport':
      return getReportCreationPresentation({
        running: options.assistantMessageRunning,
        toolCall,
        uiParts: options.uiParts,
      });
    case 'generatePlaybook':
      return {
        description: 'Started generating a playbook from the attached documents.',
        title: 'Generated playbook',
      };
    case 'readPlaybook':
      return { description: 'Read the selected playbook.', title: 'Read playbook' };
    case 'readSkillResource':
      return {
        description: `Read the skill resource ${toolCall.path}.`,
        title: 'Read skill resource',
      };
    case 'searchSkillResources':
      return {
        description: `Searched the skill's files for "${toolCall.query}".`,
        title: 'Searched skill files',
      };
    case 'research':
      return {
        description: `Research plan: ${toolCall.instructions}`,
        title: 'Researched the Internet',
      };
    case 'listTasks':
      return { description: "Listed the workflow's tasks.", title: 'Listed tasks' };
    case 'reassignTask':
      return {
        description: "Updated the workflow task's assignees or reviewers.",
        title: 'Updated task assignments',
      };
    case 'runPlaybook':
      return {
        description: 'Started the selected playbook for a document.',
        title: 'Ran playbook',
      };
    case 'search':
      return { description: `Query: ${toolCall.query}.`, title: 'Searched the Internet' };
    case 'searchPlaybooks':
      return { description: `Query: ${toolCall.query}.`, title: 'Searched playbooks' };
    case 'searchSkills':
      return { description: `Query: ${toolCall.query}.`, title: 'Searched skills' };
    case 'readSkill':
      return { description: 'Read the selected skill.', title: 'Read skill' };
    case 'createSkill':
    case 'updateSkill': {
      const presentation = getSkillToolPresentation(toolCall, options);
      if (!presentation.skill)
        return { description: presentation.description ?? '', title: presentation.title };
      options.renderedSkillLinks.add(presentation.skill.id);
      return {
        description: formatSkillLink(presentation.skill, options),
        title: presentation.title,
      };
    }
    case 'selectDocuments':
      return {
        description: 'Selected repository documents for downstream agents.',
        title: 'Selected documents',
      };
    case 'semanticSearch':
      return {
        description: `Query: ${getSemanticSearchDisplayQueries(toolCall).join('; ')}.`,
        title: 'Searched repository documents',
      };
    case 'swapAgent':
      return {
        description: `Transferred from ${toolCall.fromAgent} to ${toolCall.toAgent}.`,
        title: 'Transferred agent',
      };
    case 'workflowCollectIntakeForm':
      return {
        description: toolCall.output
          ? 'Submitted the workflow intake form.'
          : 'Requested the workflow intake form.',
        title: toolCall.output ? 'Collected workflow intake' : 'Requested workflow intake',
      };
    case 'workflowCollectLaunchInfo':
      return {
        description: toolCall.output
          ? 'Submitted the workflow launch information.'
          : 'Requested workflow launch information.',
        title: toolCall.output
          ? 'Collected workflow launch information'
          : 'Requested launch information',
      };
    case 'workflowCreateDraft':
      if (toolCall.preparing)
        return {
          description: 'Building the workflow structure for review.',
          title: 'Preparing workflow details',
        };
      if (toolCall.result?.status === 'errored')
        return {
          description: toolCall.result.errorMessage,
          title: 'Could not create draft workflow',
        };
      return {
        description: getCreateDraftMarkdownDescription(toolCall, options),
        title: 'Created draft workflow',
      };
    case 'workflowLaunchTemplate': {
      const metadata = options.workflowTemplateArtifactsById.get(toolCall.templateWorkflowId);
      return {
        description: metadata
          ? `Launched "${toolCall.workflowName}" from template "${metadata.templateTitle}"`
          : `Launched "${toolCall.workflowName}" from the selected template.`,
        title: 'Launch workflow',
      };
    }
    case 'workflowSearchExisting':
      return {
        description: `Query: ${toolCall.retrievalQueries.join('; ')}.`,
        title: 'Searched existing workflows',
      };
    case 'workflowSearchTemplates':
      return {
        description: `Query: ${toolCall.retrievalQueries.join('; ')}.`,
        title: 'Searched workflow templates',
      };
    case 'externalTool': {
      /*
       * The criteria stand on their own line rather than replacing the outcome:
       * what was asked for and what came back are different claims, and an
       * export that keeps only one of them cannot be checked against the other.
       */
      /*
       * The criteria are model-authored and the toolkit, slugs and emails come
       * out of a vendor payload, so both lines are escaped the way link labels
       * are. A bracket in a search term would otherwise reshape the export.
       */
      const searched = toolCall.criteria
        ? `Search: ${escapeMarkdownLinkLabel(toolCall.criteria)}.`
        : null;
      const outcome = escapeMarkdownLinkLabel(
        toolCall.resolved
          ? getExternalToolResolutionLabel(toolCall.resolved)
          : `${getExternalToolkitLabel(toolCall.toolkit)}: ${toolCall.toolSlugs.join(', ')}.`
      );
      return {
        description: [searched, outcome].filter(Boolean).join(' '),
        title: getExternalToolCallTitle({
          toolSlugs: toolCall.toolSlugs,
          toolkit: toolCall.toolkit,
        }),
      };
    }
    /*
     * No link in the export. The one the user followed was minted for that
     * click and expired minutes later, so printing it would only offer a
     * reader something that no longer works.
     */
    case 'externalConnect':
      return {
        description: getExternalConnectDescription(toolCall.toolkit),
        title: getExternalConnectTitle(toolCall.toolkit),
      };
    case 'readWorkflow':
      return {
        description: toolCall.workflowId
          ? `Read structure and status for ${formatWorkflowToolLink(toolCall.workflowId, options)}.`
          : 'Read workflow structure and status.',
        title: 'Read workflow',
      };
    case 'managePhases':
      return { description: `Operation: ${toolCall.operation}.`, title: 'Managed workflow phases' };
    case 'assignTask':
      return {
        description:
          toolCall.taskId && toolCall.workflowId
            ? `Assigned ${formatAbsoluteMarkdownLink(
                getTaskArtifact(toolCall.taskId, options)?.taskTitle || 'task',
                link.WorkflowDetailTabTask({
                  taskId: toolCall.taskId,
                  workflowId: toolCall.workflowId,
                }),
                options
              )}.`
            : 'Assigned a workflow task.',
        title: 'Assigned task',
      };
    case 'updateIntakeForm':
      return { description: 'Updated the workflow intake form.', title: 'Updated intake form' };
    case 'updateTaskDescription':
      return {
        description: 'Updated the workflow task description.',
        title: 'Updated task description',
      };
  }
}

function getCreatedWorkflowTitle(
  toolCall: Extract<ToolCall, { type: 'workflowCreateDraft' }>,
  options: SerializationContext
) {
  const result = toolCall.result;
  if (result?.status !== 'created_draft') return null;
  return (
    getWorkflowArtifact(result.workflow.workflowId, options)?.workflowTitle || 'Untitled Workflow'
  );
}

function getCreateDraftMarkdownDescription(
  toolCall: Extract<ToolCall, { type: 'workflowCreateDraft' }>,
  options: SerializationContext
) {
  const workflowName =
    getCreatedWorkflowTitle(toolCall, options) ?? toolCall.workflowName ?? 'Untitled Workflow';
  return `"${workflowName}".`;
}

function getWorkflowArtifact(workflowId: string, options: SerializationContext) {
  return options.workflowArtifactsById.get(workflowId);
}

function getTaskArtifact(taskId: string, options: SerializationContext) {
  return options.taskArtifactsById.get(taskId);
}

function formatWorkflowToolLink(workflowId: string, options: SerializationContext) {
  const metadata = getWorkflowArtifact(workflowId, options);
  return formatAbsoluteMarkdownLink(
    metadata?.workflowTitle || 'workflow',
    getWorkflowRoute(workflowId),
    options
  );
}

const WORKFLOW_RESULT_TITLES: Record<WorkflowAiResult['status'], string> = {
  created_draft: '*Created draft workflow*',
  duplicates_found: '*Found existing workflows*',
  errored: '*Workflow tool failed*',
  launched: '*Launched workflow*',
  no_match: '*No matching workflow found*',
  template_candidates: '*Found workflow templates*',
};

function formatWorkflowResultBlock(rawJson: string, options: SerializationContext) {
  const parsedResult = WorkflowAiResultSchema.safeParse(parseJson(rawJson));
  if (!parsedResult.success) return '*Workflow result unavailable*';

  const result = parsedResult.data;
  return [
    WORKFLOW_RESULT_TITLES[result.status],
    result.reasoningSummary,
    getWorkflowResultBody(result, options),
  ]
    .filter(Boolean)
    .join('\n');
}

function getWorkflowResultBody(result: WorkflowAiResult, options: SerializationContext) {
  switch (result.status) {
    case 'created_draft':
    case 'launched': {
      const metadata = getWorkflowArtifact(result.workflow.workflowId, options);
      return formatAbsoluteMarkdownLink(
        metadata?.workflowTitle || 'Untitled Workflow',
        getWorkflowRoute(result.workflow.workflowId),
        options
      );
    }
    case 'duplicates_found':
    case 'no_match':
    case 'template_candidates':
      return formatWorkflowCandidates(result.candidates, options);
    case 'errored':
      return result.errorMessage;
  }
}

function formatInlineWorkflowSearchResult(toolCall: ToolCall, options: SerializationContext) {
  const result = getWorkflowSearchToolCallResult(toolCall);
  if (!result) return '';
  if (result.status === 'errored') return result.errorMessage;

  const candidates = getWorkflowResultCandidates(result);
  if (!candidates.length) return '';

  return [
    `Found ${candidates.length} search ${pluralize('result', candidates.length)}`,
    formatWorkflowCandidates(candidates, options),
  ].join('\n');
}

function getWorkflowSearchToolCallResult(toolCall: ToolCall): WorkflowAiToolCallResult | null {
  if (!getIsWorkflowSearchToolCall(toolCall)) return null;
  return toolCall.result ?? null;
}

function getIsWorkflowSearchToolCall(
  toolCall: ToolCall
): toolCall is Extract<ToolCall, { type: 'workflowSearchExisting' | 'workflowSearchTemplates' }> {
  return toolCall.type === 'workflowSearchExisting' || toolCall.type === 'workflowSearchTemplates';
}

function getSemanticSearchDisplayQueries(toolCall: Extract<ToolCall, { type: 'semanticSearch' }>) {
  if (toolCall.retrievalQueries?.length) return toolCall.retrievalQueries;
  if (toolCall.query) return [toolCall.query];
  return [];
}

function getQueryDocumentDisplayQueries(toolCall: Extract<ToolCall, { type: 'queryDocument' }>) {
  if (toolCall.retrievalQueries?.length) return toolCall.retrievalQueries;
  if (toolCall.question) return [toolCall.question];
  return [];
}

function getWorkflowResultCandidates(
  result: WorkflowAiResult | WorkflowAiToolCallResult
): WorkflowAiCandidate[] {
  switch (result.status) {
    case 'duplicates_found':
    case 'no_match':
    case 'template_candidates':
      return result.candidates;
    case 'created_draft':
    case 'errored':
    case 'launched':
      return [];
  }
}

function formatWorkflowCandidates(
  candidates: WorkflowAiCandidate[],
  options: SerializeAiChatMarkdownOptions
) {
  return candidates
    .map(candidate => {
      const link = formatAbsoluteMarkdownLink(candidate.title, candidate.url, options);
      return `- ${link} - ${candidate.confidenceReason}`;
    })
    .join('\n');
}

function formatAbsoluteMarkdownLink(
  label: string,
  path: string,
  options: SerializeAiChatMarkdownOptions
) {
  return `[${escapeMarkdownLinkLabel(label)}](${getAbsoluteAppUrl(path, options.baseAppUrl)})`;
}

function formatLink(
  label: string,
  url: string,
  options: SerializeAiChatMarkdownOptions,
  mode: SerializationMode
) {
  /*
   * Canonical mention labels are "@"-prefixed ("[@Name](mention:...)"). Chat
   * channel adapters rewrite bare "@word" into provider mention syntax (Slack:
   * "<@word>"), which mangles the label into a broken literal mention — so the
   * "@" is dropped for every non-copy surface.
   */
  const displayLabel =
    url.startsWith('mention:') && mode !== 'copy' ? label.replace(/^@/, '') : label;
  const escapedLabel = escapeMarkdownLinkLabel(displayLabel);
  if (mode === 'plain-text') return displayLabel;

  const mentionPath = getMentionAppPath(url);
  if (mentionPath) {
    return `[${escapedLabel}](${getAbsoluteAppUrl(mentionPath, options.baseAppUrl)})`;
  }
  if (url.startsWith('mention:')) return displayLabel;

  const location = url.startsWith('#doc/') ? parseMarkdownLocationLink(url) : null;
  if (location) {
    const documentUrl = getAbsoluteAppUrl(
      getDocumentLocationRoute(location.documentId, location.locationId),
      options.baseAppUrl
    );
    return `[${escapedLabel}](${documentUrl})`;
  }
  if (url.startsWith('#doc/') || url.startsWith('#col/')) return label;

  if (url.startsWith('/') && !url.startsWith('//')) {
    return `[${escapedLabel}](${getAbsoluteAppUrl(url, options.baseAppUrl)})`;
  }

  const parsedUrl = safeParseUrl(url);
  if (!parsedUrl) return label;
  if (parsedUrl.protocol !== 'http:' && parsedUrl.protocol !== 'https:') return label;

  return null;
}

function getMentionAppPath(url: string) {
  if (!url.startsWith('mention:')) return null;

  const mentionPath = url.slice('mention:'.length).split('?')[0];
  if (!mentionPath) return null;
  const slashIndex = mentionPath.indexOf('/');
  if (slashIndex === -1) return null;

  const type = mentionPath.slice(0, slashIndex);
  const rawId = mentionPath.slice(slashIndex + 1);
  if (!rawId) return null;

  if (
    type === 'document' ||
    type === 'selection' ||
    type === 'selection-v2' ||
    type === 'selection-v3'
  ) {
    const documentId = rawId.split('/')[0];
    return documentId ? getDocumentRoute(documentId) : null;
  }
  if (type === 'workflow') return getWorkflowRoute(rawId);

  return null;
}

function getDocumentLocationRoute(documentId: string, locationId: string) {
  return `${getDocumentRoute(documentId)}#${encodeURIComponent(locationId)}`;
}

function getAbsoluteAppUrl(path: string, baseAppUrl: string) {
  return new URL(path, baseAppUrl).toString();
}

function safeParseUrl(value: string) {
  try {
    return new URL(value);
  } catch {
    return null;
  }
}

function getNodeRange(node: PositionedNode) {
  const start = node.position?.start.offset;
  const end = node.position?.end.offset;
  if (typeof start !== 'number' || typeof end !== 'number') return null;
  return { end, start };
}

function applyMarkdownReplacements(text: string, replacements: MarkdownReplacement[]) {
  return [...replacements]
    .sort((a, b) => b.start - a.start)
    .reduce(
      (result, replacement) =>
        result.slice(0, replacement.start) + replacement.value + result.slice(replacement.end),
      text
    );
}

function getNodeText(node: Link) {
  return node.children.map(getPhrasingContentText).join('');
}

function getPhrasingContentText(node: Link['children'][number]): string {
  switch (node.type) {
    case 'text':
    case 'inlineCode':
      return node.value;
    case 'emphasis':
    case 'strong':
    case 'delete':
    case 'link':
    case 'linkReference':
      return node.children.map(getPhrasingContentText).join('');
    case 'image':
    case 'imageReference':
      return node.alt ?? '';
    case 'break':
      return '\n';
    case 'footnoteReference':
      return node.label ?? node.identifier;
    case 'html':
      return '';
    default:
      return '';
  }
}

export function escapeMarkdownLinkLabel(label: string) {
  return label.replaceAll('\\', '\\\\').replaceAll('[', '\\[').replaceAll(']', '\\]');
}

function parseJson(text: string) {
  try {
    const jsonSchema = z.json();
    return jsonSchema.parse(JSON.parse(text));
  } catch {
    return null;
  }
}
